import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';

import type IStatusDropButton from './interfaces/IStatusDropButton';

import styles from './sass/StatusDropButton.module.scss';

function StatusDropButton({
  options, title, labelKey, hideSearch, selectedOptions, onSelectionChange, translate, className,
  classNameList, clearAllSelected, resetFunction, showSelectAll, addAllSelected, isDisabled,
}: IStatusDropButton) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const onHandleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCheckboxChange = (optionValue: string) => {
    const currentSelected = selectedOptions || [];
    const isSelected = currentSelected.includes(optionValue);
    let newSelectedOptions: string[];

    if (isSelected) {
      newSelectedOptions = currentSelected.filter((option) => option !== optionValue);
    } else {
      newSelectedOptions = [...currentSelected, optionValue];
    }

    onSelectionChange(newSelectedOptions);
    resetFunction?.();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredOptions = options?.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className={styles.root} ref={dropdownRef}>
      <button
        type="button"
        className={clsx(styles.button, className, {
          [styles.buttonOpen]: isOpen,
          [styles.buttonDisabled]: isDisabled,
        })}
        disabled={isDisabled}
        onClick={onHandleOpen}
        data-i18n-key={labelKey}
      >
        <div className={styles.titleWrapper}>
          {title ? <span>{title}</span> : null}
          {selectedOptions && selectedOptions.length > 0
            ? <span className={styles.selectedOptions}>{selectedOptions.length}</span>
            : null}
        </div>
        <ChevronIcon className={clsx(styles.chevron, {
          [styles.chevronOpen]: isOpen,
        })}
        />
      </button>
      <div className={clsx(styles.dropdown, classNameList, {
        [styles.dropdownOpen]: isOpen,
      })}
      >
        <div className={styles.dropdownContent}>
          <div className={styles.dropdownHeader}>
            {!hideSearch && (
              <div className={styles.searchWrapper}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={t('common.searchByName')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            )}
            {showSelectAll ? (
              <Button
                title={t('taskManagement.selectAll')}
                variant="ghost"
                onClick={addAllSelected}
              />
            ) : null}
            <Button
              title={t('taskManagement.unselectAll')}
              variant="ghost"
              onClick={clearAllSelected}
            />
          </div>
          <div className={styles.dropdownItems}>
            {filteredOptions?.map((option, index) => (
              <div key={[option, index].join('_')} className={styles.dropdownItem}>
                <Checkbox
                  label={translate ? t(`common.${option}`) : option}
                  name={option}
                  value={option}
                  checked={(selectedOptions || []).includes(option)}
                  onChange={() => handleCheckboxChange(option)}
                />
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

export default StatusDropButton;
